import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../utils/api'
import { fmtDuration } from './ChatHistoryCompaniesView'

interface AgentRow {
  agent_id: number | string
  name: string
  avatar_url: string | null
  seconds: number
  chats: number
}
interface CompanyRow {
  alias: string
  company_name: string
  company_logo_url?: string | null
  total_chats: number
  total_time_seconds: number
  avg_resolution_seconds: number | null
  last_chat_at: string | null
}
interface MonthlyData {
  month: string
  total_time_seconds: number
  total_chats: number
  agents: AgentRow[]
  companies: CompanyRow[]
}

const COMPANY_SORTS: Array<[string, string]> = [
  ['latest', 'Latest activity'],
  ['oldest', 'Oldest activity'],
  ['most_time', 'Most time'],
  ['least_time', 'Least time'],
]

function currentMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}
const ms = (v: string | null) => (v ? new Date(v).getTime() : 0)

export const MonthlyReport: React.FC = () => {
  const navigate = useNavigate()
  const [month, setMonth] = useState<string>(currentMonth())
  const [data, setData] = useState<MonthlyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [companySort, setCompanySort] = useState('most_time')

  useEffect(() => {
    let active = true
    setLoading(true)
    apiGet<MonthlyData>(`/chat-history/monthly?month=${encodeURIComponent(month)}`)
      .then((d) => { if (active) { setData(d); setError(null) } })
      .catch(() => { if (active) setError('Failed to load the monthly report') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [month])

  const companies = useMemo(() => {
    const list = [...(data?.companies || [])]
    const sorters: Record<string, (a: CompanyRow, b: CompanyRow) => number> = {
      latest: (a, b) => ms(b.last_chat_at) - ms(a.last_chat_at),
      oldest: (a, b) => ms(a.last_chat_at) - ms(b.last_chat_at),
      most_time: (a, b) => b.total_time_seconds - a.total_time_seconds,
      least_time: (a, b) => a.total_time_seconds - b.total_time_seconds,
    }
    list.sort(sorters[companySort] || sorters.most_time)
    return list
  }, [data, companySort])

  const maxAgentSeconds = useMemo(
    () => Math.max(1, ...(data?.agents || []).map((a) => a.seconds)),
    [data],
  )

  return (
    <div>
      {/* Month filter + totals */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <label className="flex flex-col gap-1 text-xs text-txt-secondary">
          Month
          <input
            type="month"
            value={month}
            max={currentMonth()}
            onChange={(e) => setMonth(e.target.value || currentMonth())}
            className="px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary"
          />
        </label>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-border bg-surface px-4 py-3 min-w-[130px]">
            <div className="text-2xl font-bold text-txt-primary leading-none">{fmtDuration(data?.total_time_seconds)}</div>
            <div className="text-[10px] uppercase tracking-wide text-txt-tertiary mt-1">Time on chats · {monthLabel(month)}</div>
          </div>
          <div className="rounded-xl border border-border bg-surface px-4 py-3 min-w-[100px]">
            <div className="text-2xl font-bold text-txt-primary leading-none">{data?.total_chats ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-txt-tertiary mt-1">Chats</div>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: per-agent handling time */}
          <section>
            <h2 className="text-sm font-semibold text-txt-primary mb-3">Agent time</h2>
            <div className="rounded-xl border border-border bg-surface divide-y divide-border overflow-y-auto max-h-[560px]">
              {(data?.agents || []).length === 0 ? (
                <p className="text-sm text-txt-tertiary p-4">No agent activity this month.</p>
              ) : (
                (data?.agents || []).map((a) => (
                  <button
                    key={a.agent_id}
                    onClick={() => navigate(`/chat-history/agent/${encodeURIComponent(String(a.agent_id))}?month=${encodeURIComponent(month)}`)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors"
                  >
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-semibold shrink-0">
                        {(a.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-txt-primary truncate">{a.name}</span>
                        <span className="text-sm font-semibold text-txt-primary tabular-nums shrink-0">{fmtDuration(a.seconds)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                          <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.round((a.seconds / maxAgentSeconds) * 100)}%` }} />
                        </div>
                        <span className="text-[11px] text-txt-tertiary shrink-0 tabular-nums">{a.chats} chat{a.chats === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>

          {/* Right: company cards list (sortable), scoped to the month */}
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-sm font-semibold text-txt-primary">Companies</h2>
              <label className="flex items-center gap-2 text-xs text-txt-secondary">
                Sort
                <select
                  value={companySort}
                  onChange={(e) => setCompanySort(e.target.value)}
                  className="px-2.5 py-1.5 rounded-lg border border-border bg-surface text-xs text-txt-primary"
                >
                  {COMPANY_SORTS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="rounded-xl border border-border bg-surface divide-y divide-border overflow-y-auto max-h-[560px]">
              {companies.length === 0 ? (
                <p className="text-sm text-txt-tertiary p-4">No company chats this month.</p>
              ) : (
                companies.map((c) => (
                  <button
                    key={c.alias}
                    onClick={() => navigate(`/chat-history/${encodeURIComponent(c.alias)}`)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors"
                  >
                    {c.company_logo_url ? (
                      <img src={c.company_logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-white border border-border shrink-0" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-semibold shrink-0">
                        {(c.company_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-txt-primary truncate" title={c.company_name}>{c.company_name}</div>
                      <div className="text-[11px] text-txt-tertiary">
                        {c.last_chat_at ? `Last ${new Date(c.last_chat_at).toLocaleDateString()}` : ' '}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-txt-primary tabular-nums">{fmtDuration(c.total_time_seconds)}</div>
                      <div className="text-[11px] text-txt-tertiary tabular-nums">{c.total_chats} chat{c.total_chats === 1 ? '' : 's'}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
